import { notification } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { EmpresaSlugProdutoPacoteBarra } from '@/features/empresa-slug/types/EmpresaSlugProduto';
import { apiEmpresaSlugClienteRenovarToken } from '@/features/empresa-slug/api/apiEmpresaSlugClienteRenovarToken';
import { SistemaIntegradoIdpk } from '@/features/sistema-integrado/enum';
import { router } from '@/router/Router';
import { DATE_TIME_FORMAT } from '@/std/const/date';
import { uuid } from '@/std/util';
import type { CatalogoProduto } from '../types/CatalogoProduto';
import type { CatalogoStateEmpresa, CatalogoStore } from './types';
import { CheckoutSteps } from '../types/CheckoutSteps';

dayjs.extend(customParseFormat);

const modalProdutoClosed = {
    mode: null,
    open: false,
    produto: null,
    videoPlaying: false,
} as const;

const createEmptyEmpresa = (): CatalogoStateEmpresa => ({
    cadastro: null,
    carrinho: [],
    login: null,
});

export const useCatalogoStore = create<CatalogoStore>()(
    persist(
        immer(
            // biome-ignore assist/source/useSortedKeys: manually sorted keys
            (set, get) => ({
                empresaIdpk: null,

                getEmpresaIdpk(): number {
                    const empresaIdpk = get().empresaIdpk;

                    if (!empresaIdpk) {
                        throw new Error('Empresa IDPK não está definida no store do catálogo');
                    }

                    return empresaIdpk;
                },

                // Estado por empresa
                empresas: {},

                getEmpresa(): CatalogoStateEmpresa {
                    const empresaIdpk = get().getEmpresaIdpk();
                    let state = get().empresas[empresaIdpk];

                    if (!state) {
                        state = createEmptyEmpresa();

                        set((draft) => {
                            draft.empresas[empresaIdpk] = state as CatalogoStateEmpresa;
                        });
                    }

                    return state;
                },

                getOrCreateEmpresaState(state: CatalogoStore): CatalogoStateEmpresa {
                    const empresaIdpk = state.getEmpresaIdpk();
                    let empState = state.empresas[empresaIdpk];
                    if (!empState) {
                        empState = createEmptyEmpresa();
                        state.empresas[empresaIdpk] = empState;
                    }
                    return empState;
                },

                updateEmpresa: (empresaState: Partial<CatalogoStateEmpresa>): void => {
                    const empresaIdpk = get().getEmpresaIdpk();

                    set((state) => {
                        let empState = state.empresas[empresaIdpk];

                        if (!empState) {
                            empState = createEmptyEmpresa();
                            state.empresas[empresaIdpk] = empState;
                        }

                        Object.assign(empState, empresaState);
                    });
                },

                resetEmpresa: (): void => {
                    const empresaIdpk = get().getEmpresaIdpk();
                    set((state) => {
                        state.empresas[empresaIdpk] = createEmptyEmpresa();
                    });
                },

                // Login
                renovandoToken: false,

                isUserLogged: (): boolean => {
                    const empresa = get().getEmpresa();
                    return !!empresa.login;
                },

                getTokenStatus: () => {
                    const empresa = get().getEmpresa();
                    const login = empresa.login;

                    if (!login?.token_exp || !login.token) {
                        return { status: 'expired', token: null };
                    }

                    const tokenExp = dayjs(login.token_exp, DATE_TIME_FORMAT);

                    if (!tokenExp.isValid()) {
                        return { status: 'expired', token: null };
                    }

                    const now = dayjs();
                    const diff = tokenExp.diff(now, 'minutes');

                    if (diff <= 0) {
                        return { status: 'expired', token: null };
                    }

                    if (diff <= 4 * 60) {
                        return { status: 'about-to-expire', token: login.token };
                    }

                    return { status: 'valid', token: login.token };
                },

                renewToken: async () => {
                    const empresaIdpk = get().getEmpresaIdpk();
                    const renovandoToken = get().renovandoToken;

                    if (renovandoToken) {
                        return;
                    }

                    set({ renovandoToken: true });

                    try {
                        const response = await apiEmpresaSlugClienteRenovarToken({
                            empresa_idpk: empresaIdpk,
                            sistema_integrado_idpk: SistemaIntegradoIdpk.CatalogoDigital,
                        });

                        set((state) => {
                            const empState = state.empresas[empresaIdpk];

                            if (empState?.login) {
                                empState.login.token = response.token;
                                empState.login.token_exp = response.token_exp;
                            }

                            state.renovandoToken = false;
                        });
                    } catch (e) {
                        notification.error({
                            description: e.message,
                            title: 'Falha ao renovar token',
                        });

                        set((state) => {
                            const empState = state.empresas[empresaIdpk];

                            if (empState) {
                                empState.cadastro = null;
                                empState.login = null;
                            }

                            state.renovandoToken = false;
                        });

                        void router.navigate({
                            params: (p) => ({ slug: p.slug || '' }),
                            to: '/p/catalogo/$slug/auth/entrar',
                        });
                    }
                },

                // Drawer informação
                showDrawerInformacao: false,

                openDrawerInformacao: () => {
                    set({ showDrawerInformacao: true });
                },

                closeDrawerInformacao: () => {
                    set({ showDrawerInformacao: false });
                },

                // Filtros e paginação
                filter: {
                    colunas: 0,
                    orderby: 'pro_descricao asc',
                },

                applyFilter: (filter) => {
                    set((s) => ({
                        filter: {
                            ...s.filter,
                            ...filter,
                            pro_idpk: undefined,
                        },
                        pagination: {
                            ...s.pagination,
                            current: 1,
                        },
                    }));
                },

                pagination: {
                    current: 1,
                    pageSize: 60,
                    total: 0,
                },

                setPagination: (pagination) => {
                    set((s) => ({
                        pagination: {
                            ...s.pagination,
                            ...pagination,
                        },
                    }));
                },

                // Modal produto

                modalProduto: modalProdutoClosed,

                closeModalProduto: () => {
                    set({ modalProduto: modalProdutoClosed });
                },

                openModalAddProduto: (produto, videoPlaying) => {
                    const produtoWithQuantidade: CatalogoProduto = {
                        ...produto,
                        adicional:
                            produto.adicional?.map((adicional) => ({
                                ...adicional,
                                item: adicional.item.map((item) => ({
                                    ...item,
                                    quantidade: 0,
                                })),
                            })) || [],
                        key: uuid(),
                        pacoteBarraSelecionado: null,
                        quantidade: 1,
                    };

                    set({
                        modalProduto: {
                            mode: 'add',
                            open: true,
                            produto: produtoWithQuantidade,
                            videoPlaying,
                        },
                    });
                },

                openModalEditProduto: (produto) => {
                    set({
                        modalProduto: {
                            mode: 'edit',
                            open: true,
                            produto,
                            videoPlaying: false,
                        },
                    });
                },

                modalProdutoSetQuantidade: (quantidade: number) => {
                    set((state) => {
                        const produto = state.modalProduto.produto;

                        if (!produto) {
                            return;
                        }

                        let newQuantidade: number;

                        if (produto.permite_venda_fracionada) {
                            const q = Number.isFinite(quantidade) ? quantidade : 0;
                            // mínimo 0.01 e arredonda para 2 casas se necessário
                            const q2 = Math.max(0.01, Math.round(q * 100) / 100);
                            newQuantidade = q2;
                        } else {
                            const q = Number.isFinite(quantidade) ? quantidade : 1;
                            newQuantidade = Math.max(1, Math.floor(q));
                        }

                        produto.quantidade = newQuantidade;
                    });
                },

                modalProdutoSetObservacao: (observacao: string) => {
                    set((state) => {
                        if (state.modalProduto.produto) {
                            state.modalProduto.produto.observacao = observacao;
                        }
                    });
                },

                modalProdutoSetQuantidadeAdicionalItem: (pra_idpk, pai_idpk, quantidade) => {
                    set((state) => {
                        if (!state.modalProduto.open || !state.modalProduto.produto) {
                            return;
                        }

                        const adicional = state.modalProduto.produto.adicional?.find(
                            (a) => a.pra_idpk === pra_idpk,
                        );

                        if (!adicional) {
                            return;
                        }

                        const item = adicional.item?.find((i) => i.pai_idpk === pai_idpk);

                        if (!item) {
                            return;
                        }

                        const quantidadeItens = adicional.item
                            .filter((i) => i.pai_idpk !== pai_idpk)
                            .reduce((sum, i) => sum + (i.quantidade || 0), 0);

                        const safeQuantidade = Math.max(0, Math.floor(quantidade));
                        const quantidadeTotal = quantidadeItens + safeQuantidade;

                        if (
                            adicional.pra_quantidade_maxima &&
                            quantidadeTotal > adicional.pra_quantidade_maxima
                        ) {
                            const restante = adicional.pra_quantidade_maxima - quantidadeItens;
                            item.quantidade = Math.max(0, restante);
                        } else {
                            item.quantidade = safeQuantidade;
                        }
                    });
                },

                modalProdutoSetPacote: (pacote: EmpresaSlugProdutoPacoteBarra | null): void => {
                    set((state) => {
                        if (!state.modalProduto.open) {
                            return;
                        }

                        if (state.modalProduto.produto) {
                            state.modalProduto.produto.pacoteBarraSelecionado = pacote;
                        }
                    });
                },

                modalProdutoUpdateAdicionalUnico: (pra_idpk: string, pai_idpk: string): void => {
                    set((state) => {
                        if (!state.modalProduto.open || !state.modalProduto.produto) {
                            return;
                        }

                        const adicional = state.modalProduto.produto.adicional?.find(
                            (a) => a.pra_idpk === pra_idpk,
                        );

                        if (!adicional?.item) {
                            return;
                        }

                        for (const item of adicional.item) {
                            if (item.pai_idpk === pai_idpk) {
                                item.quantidade = item.quantidade === 1 ? 0 : 1;
                            } else {
                                item.quantidade = 0;
                            }
                        }
                    });
                },

                // Carrinho

                showDrawerCarrinho: false,

                closeDrawerCarrinho: () => {
                    set({ showDrawerCarrinho: false });
                },

                getProdutosCarrinho: (): CatalogoProduto[] => {
                    return get().getEmpresa().carrinho;
                },

                adicionarProdutoCarrinho: (produto) => {
                    set((state) => {
                        const empState = state.getOrCreateEmpresaState(state);
                        empState.carrinho.push(produto);
                    });
                },

                atualizarProdutoCarrinho: (key, produto) => {
                    set((state) => {
                        const empState = state.getOrCreateEmpresaState(state);
                        const index = empState.carrinho.findIndex((p) => p.key === key);

                        if (index !== -1) {
                            const newProduto = {
                                ...empState.carrinho[index],
                                ...produto,
                            } as CatalogoProduto;

                            empState.carrinho[index] = newProduto;

                            if (empState.carrinho[index].quantidade <= 0) {
                                empState.carrinho.splice(index, 1);
                            }
                        }
                    });
                },

                atualizarProdutosCarrinho: (produtos) => {
                    set((state) => {
                        const empState = state.getOrCreateEmpresaState(state);
                        empState.carrinho = produtos;
                    });
                },

                removerProdutoCarrinho: (key) => {
                    set((state) => {
                        const empState = state.getOrCreateEmpresaState(state);
                        const index = empState.carrinho.findIndex((produto) => produto.key === key);

                        if (index !== -1) {
                            empState.carrinho.splice(index, 1);
                        }
                    });
                },

                isProdutoNoCarrinho: (pro_idpk) => {
                    const emp_idpk = get().empresaIdpk;
                    if (!emp_idpk) {
                        return false;
                    }

                    const empState = get().empresas[emp_idpk];

                    return (
                        empState?.carrinho.some((produto) => produto.pro_idpk === pro_idpk) || false
                    );
                },

                limparCarrinho: () => {
                    const emp_idpk = get().empresaIdpk;

                    if (!emp_idpk) {
                        return;
                    }

                    set((state) => {
                        const empState = state.getOrCreateEmpresaState(state);
                        empState.carrinho = [];
                    });
                },

                // Finalização

                current: 0,
                pagamentoPendente: false,
                dadosCliente: null,
                salvarDados: true,
                formaRecebimento: null,
                formaPagamento: null,

                concluirCheckout: () => {
                    window.scrollTo({ behavior: 'smooth', top: 0 });
                    const emp_idpk = get().empresaIdpk;

                    if (!emp_idpk) {
                        return;
                    }

                    set((state) => {
                        state.current = CheckoutSteps.Concluido;
                        state.pagamentoPendente = false;
                        let empState = state.empresas[emp_idpk];
                        if (!empState) {
                            empState = createEmptyEmpresa();
                            state.empresas[emp_idpk] = empState;
                        }

                        empState.carrinho = [];
                    });
                },
            }),
        ),
        {
            name: 'catalogo',
            partialize(state) {
                return {
                    dadosCliente: state.dadosCliente,
                    empresas: state.empresas,
                    salvarDados: state.salvarDados,
                };
            },
        },
    ),
);
